import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './components/auth/LoginPage';
import { Dashboard } from './components/admin/Dashboard';
import LandingPage from './components/LandingPage';
import { ProjectForm } from './components/admin/project/ProjectForm';
import { PageBuilder } from './components/admin/project/PageBuilder';
import DynamicLandingPage from './components/landing/DynamicLandingPage';
import BrochureDownload from './components/landing/BrochureDownload';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import { AuthProvider, useAuth } from './lib/auth/AuthContext';
import { ToastProvider } from './components/ui/use-toast';
import CookieConsent from './components/ui/CookieConsent';
import ConsentDebugger from './components/ui/ConsentDebugger';
// Import our custom components instead of Convex's
import { Authenticated, Unauthenticated, AuthLoading } from './lib/auth/auth-components';

import './App.css';
import { AuthTest } from './components/admin/AuthTest';

// Loading spinner component for better user experience
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="relative flex flex-col items-center">
      <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
      <p className="mt-4 text-gray-600">Loading application...</p>
    </div>
  </div>
);

// Secure route that uses both our AuthContext and Convex's Authenticated component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Short-circuit if still loading - show nothing instead of a loading indicator
  // This avoids flashing loading states for quick loads
  if (isLoading && typeof window !== 'undefined') {
    // Check if we've been loading for more than 1 second
    const startTime = window.sessionStorage.getItem('auth_loading_start');
    const now = Date.now();
    
    if (!startTime) {
      window.sessionStorage.setItem('auth_loading_start', now.toString());
      return null;
    }
    
    const elapsed = now - parseInt(startTime);
    // Only show loading indicator if it's taken more than 1 second
    if (elapsed < 1000) {
      return null;
    }
    
    return <LoadingSpinner />;
  } else if (!isLoading && typeof window !== 'undefined') {
    // Clear the loading start time
    window.sessionStorage.removeItem('auth_loading_start');
  }
  
  // Then wrap in Convex's Authenticated and Unauthenticated components
  return (
    <>
      <Authenticated>
        {children}
      </Authenticated>
      <Unauthenticated>
        <Navigate to="/login" replace />
      </Unauthenticated>
    </>
  );
};

// Public route that is accessible whether user is authenticated or not
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// Login route that redirects to dashboard if already authenticated
const LoginRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return null; // Show nothing during short loads
  }
  
  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {  
  return (
    <div className="App">
      <AuthLoading>
        <LoadingSpinner />
      </AuthLoading>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
      
      {/* Development Debugger */}
      <ConsentDebugger />
      
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login" element={<LoginRoute><LoginPage /></LoginRoute>} />
        <Route path="/privacy-policy" element={<PublicRoute><PrivacyPolicy /></PublicRoute>} />
        <Route path="/:language/:projectSlug" element={<PublicRoute><DynamicLandingPage /></PublicRoute>} />
        <Route path="/api/download-brochure/:projectId/:language" element={<PublicRoute><BrochureDownload /></PublicRoute>} />
        
        {/* Admin routes */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/projects"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/new"
          element={
            <ProtectedRoute>
              <ProjectForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/:projectId/edit"
          element={
            <ProtectedRoute>
              <ProjectForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/:projectId/builder"
          element={
            <ProtectedRoute>
              <PageBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/auth-test"
          element={
            <ProtectedRoute>
              <AuthTest />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App; 